import React, { useContext, useEffect, useState, type JSX } from 'react';
import { useRouter } from 'next/router';

const initialHistoryContextState = {
  history: [],
  back: () => {
    throw new Error('You forgot to wrap this in a HistoryContextProvider object');
  },
};

interface ContextShape {
  history: Array<string>;
  back(): void;
}

const HistoryContext = React.createContext<ContextShape>(initialHistoryContextState);

// https://stackoverflow.com/questions/55565631/how-to-get-previous-url-in-next-js
export const HistoryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { asPath, push, pathname } = useRouter();
  const [history, setHistory] = useState<Array<string>>([]);

  function back() {
    for (let i = history.length - 2; i >= 0; i--) {
      const route = history[i];
      if (!route.includes('#') && route !== pathname) {
        push(route);

        // if you want to pop history on back
        const newHistory = history.slice(0, i);
        setHistory(newHistory);

        break;
      }
    }
  }

  useEffect(() => {
    setHistory((previous) => [...previous, asPath]);
  }, [asPath]);

  return (
    <HistoryContext.Provider
      value={{
        back,
        history,
      }}
    >
      {children}
    </HistoryContext.Provider>
  );
};

export const useHistoryContext = (): ContextShape => useContext(HistoryContext);
